import type { ReactNode } from 'react';
import type { SupplierRecordTab } from '../model/supplier-record-tabs';
import { statusBadgeVariant } from '@crm/entities/account';
import { ArrowLeft, MoreHorizontal, Save, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { joinCss } from '@/shared/lib/css-utils';
import {
  Badge,
  Button,
  ContextMenu,
  Heading,
  MenuItem,
  Text,
} from '@/shared/ui';
import { SUPPLIER_RECORD_TABS } from '../model/supplier-record-tabs';
import css from './supplier-record-shell.module.scss';

// Counts are optional per tab: a tab whose query has not resolved yet passes
// `undefined` so the strip shows no pill at all rather than a misleading `0`.
export type SupplierRecordCounts = Partial<
  Record<SupplierRecordTab, number | undefined>
>;

export type SupplierRecordChromeProps = {
  activeTab: SupplierRecordTab;
  children: ReactNode;
  companyId: string;
  counts?: SupplierRecordCounts;
  isDirty: boolean;
  isSaving: boolean;
  name: string;
  onBack: () => void;
  onDelete: () => void;
  onSave: () => void;
  onTabClick: (tab: SupplierRecordTab) => void;
  status: string | null;
  updatedOn: string | null;
};

const formatUpdatedOn = (updatedOn: string | null): string | null => {
  if (!updatedOn) return null;
  const parsed = new Date(updatedOn);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// Builds the tab's real href so the browser can offer "Open in new tab" and
// Cmd-click. The default tab omits `?pane=` (see SUPPLIER_RECORD_DEFAULT_TAB).
const tabHref = (companyId: string, tab: SupplierRecordTab): string =>
  tab === 'info'
    ? `/crm/accounts/${companyId}`
    : `/crm/accounts/${companyId}?pane=${tab}`;

// The record chrome — header, status line, actions and tab strip — with the
// active panel as `children`. Split out of `SupplierRecordShell` (which only
// adds `AppLayout`) so the dev playground can render it without the auth
// context the layout needs.
export const SupplierRecordChrome = ({
  activeTab,
  children,
  companyId,
  counts,
  isDirty,
  isSaving,
  name,
  onBack,
  onDelete,
  onSave,
  onTabClick,
  status,
  updatedOn,
}: SupplierRecordChromeProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const updatedLabel = formatUpdatedOn(updatedOn);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  return (
    <div className={css.root}>
      <div className={css.header}>
        <div className={css.headerMain}>
          <Button
            icon={<ArrowLeft style={{ height: 14, width: 14 }} />}
            onClick={onBack}
            size="sm"
            type="button"
            variant="ghost"
          >
            Back to Accounts
          </Button>
          <Heading as="h1" size="xl">
            {name}
          </Heading>
          <Text variant="muted">Supplier record</Text>
          <div className={css.statusLine}>
            <Badge dot variant={statusBadgeVariant(status)}>
              {status ?? 'No status'} supplier
            </Badge>
            {updatedLabel && (
              <Text size="sm" variant="muted">
                Updated {updatedLabel}
              </Text>
            )}
          </div>
        </div>

        <div className={css.headerActions} ref={menuRef}>
          <div className={css.menuWrap}>
            <Button
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              icon={<MoreHorizontal size={16} />}
              onClick={() => setMenuOpen((open) => !open)}
              size="sm"
              type="button"
              variant="ghost"
            >
              More actions
            </Button>
            {menuOpen && (
              <ContextMenu className={css.menu} role="menu">
                <MenuItem
                  destructive
                  icon={<Trash2 size={14} />}
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete();
                  }}
                  role="menuitem"
                >
                  Delete account
                </MenuItem>
              </ContextMenu>
            )}
          </div>
          <Button
            disabled={!isDirty || isSaving}
            icon={<Save style={{ height: 14, width: 14 }} />}
            onClick={onSave}
            type="button"
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {/* An underlined strip, not the shared pill TabBar: these are sub-sections
          of one record, and a second pill row directly under the CRM pill nav
          reads as a peer of it. Real <a href> so the browser still offers
          "Open in new tab" / Cmd-click. */}
      <div
        aria-label="Supplier record sections"
        className={css.tabs}
        role="tablist"
      >
        {SUPPLIER_RECORD_TABS.map(({ icon: Icon, id, label }) => {
          const active = id === activeTab;
          const count = counts?.[id];
          return (
            <a
              aria-selected={active}
              className={joinCss(css.tab, active && css.tabIsActive)}
              href={tabHref(companyId, id)}
              key={id}
              onClick={(event) => {
                if (
                  event.defaultPrevented ||
                  event.button !== 0 ||
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey
                ) {
                  return;
                }
                event.preventDefault();
                onTabClick(id);
              }}
              role="tab"
            >
              <Icon aria-hidden style={{ height: 15, width: 15 }} />
              {label}
              {count !== undefined && (
                <span className={css.count}>{count}</span>
              )}
            </a>
          );
        })}
      </div>

      <div className={css.content}>{children}</div>
    </div>
  );
};
